import styled from 'styled-components';

//Import the API
import useApi from '../../../hooks/useApi';
import { useQuery } from '@tanstack/react-query'

import useTranslation from '../../../hooks/useTranslation';
import useLocale from '../../../hooks/useLocale';

import { getNestedValue } from '../../../utils/objects/nestedValue';

import updateState from '../../../utils/objects/updateState';
import sumFields from '../../../utils/sumFields';
import { retrieveParams } from './retrieveParams';
import allocationFor from './allocationFor';
import runLogic from '../../../../../common/logic/runLogic';
import resolveParams from '../../../../../common/model/utilities/resolveParams';
import { evaluateCondition } from '../../../../../common/model/condition/evaluate';

//Import SubComponents
import extract from '../../../../../common/utils/object/extract'

import MultiList from './lists/MultiList';
import Action from './lists/Action';
import Tabs from '../../tabs/Tabs';
import Warning from '../ModalWarning';
import Loader from '../../generic/Loader';

const ModalContainer = styled.div`
`;

const Header = styled.div`
    padding: 0rem 0rem 0.5rem 0rem;
    //border-bottom: 1px solid ${props => props.theme.borderColor};
`;

const Body = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 0;
`;

const Footer = styled.div`
    padding: 1rem 0rem 1rem 0rem; 
    //border-top: 1px solid ${props => props.theme.borderColor};
`;


/**
 * The rows, out of whatever the endpoint answered with.
 *
 * A list endpoint answers with an array of documents and the rows are those
 * documents - which is every `multiSelect` up to now, and why this used to be
 * one expression inline. `bomInput` fetched a single document and took its
 * `lines`, stamping an `id` because a BOM line has none of its own, and that was
 * most of why it stayed a separate widget. Both are `source` keys now.
 *
 *   path     the rows live at this key of the answered document
 *   idField  the row's identity, when it does not carry one
 */
/**
 * The rows, out of the record the modal was opened on.
 *
 * `source.endpoint` fetches; `source.from` does not — the rows are already in
 * the form store. The reservation tabs are the case: one lists the document's
 * own `lines[].locations[]` and the other its `reservations[]`, and `tabList`
 * reached them through a `calculate` block that only it could read.
 *
 * A path with `[]` in it is flattened by `extract`, which can also copy a field
 * down from the parent row — that is how a location learns its line's
 * `itemName`. Anything else is a plain array on the record.
 */
const rowsOf = (source, record) => {
    const rows = source.from.includes('[]')
        ? extract(source.from, record, { data2Copy: source.copy })
        : record?.[source.from]
    return Array.isArray(rows) ? rows : []
}

const rowsFrom = (payload, source) => {
    const body = payload?.data

    let rows
    if (source?.path) {
        // A get-by-id answers with the document, a list endpoint with one entry
        // in an array. `bomInput` tolerated both and so does this.
        const doc = Array.isArray(body) ? body[0] : body
        rows = Array.isArray(doc?.[source.path]) ? doc[source.path] : []
    } else {
        rows = Array.isArray(body) ? body : []
    }

    if (!source?.idField) return rows
    return rows.map(row => ({ ...row, id: row[source.idField] }))
}

const MultiSelect = ({
    entity,
    validated,
    useStore,
    element,
    lineId,
    path
}) => {
    //Setting up utilities
    const { getMe } = useApi();
    const { m, t } = useTranslation();
    const { lf } = useLocale()
    const testId = `${element.testId}.${lineId+1}`

    // Get local state for constraints and parameters
    const [state, setState] = useStore(s => path.length === 0 ? s : getNestedValue(s, path));
    const [fullState, setFullState] = useStore(s => s)
    const prevData = { ...fullState }

    const load = element?.load ? element?.load : true

    const constraint = state[element.constraint]
    const linesArray = element.linesArray
    const subObject = element.subObject
    // `header` and `footer` are regions: each renders a `description` with
    // `mergeParams`, and the footer also carries a warning. `body` was not one — it
    // held the endpoint, the columns, the constraint and the list type, which is
    // the widget rather than a region of it, so it is flat on the element now
    // (33.2 step 3). `lineList` keeps a `body`, because there it really is the
    // paragraph between the header and the lines.
    const header = element?.header || null;
    const footer = element?.footer || null;
    const params = retrieveParams(header?.mergeParams || [], state, lf)
    const col = element?.columns || []
    const footerParams = retrieveParams(footer?.mergeParams || [], state, lf)
    const isSingleSelect = element?.singleSelect === true;

    const list = state?.[linesArray] || []
    const docId = fullState.id

    // One `sumField`. It was declared twice — on `footer` and on `body` — and
    // resolved as `footer ?? body`, with every config setting both to the same
    // value. It names the field on a **fetched row** that says how much that row
    // offers, which is what the allocation draws down.
    //
    // It said `quantity`, and an itemLocation's `quantity` is what is on hand —
    // `availableQuantity` is that minus what other documents hold. A location
    // with 2 on hand and 1 reserved elsewhere offered 2, so a line wanting 2 took
    // both and oversold the one that was free. The column beside it and the
    // `available` partition had always read `availableQuantity`.
    const sumFieldName = element?.sumField || '';

    // Compute existing selections based on linesArray or subObject
    const existingSelectionsComputed = linesArray
        ? (state?.[linesArray] || [])
        : (subObject && state?.[subObject] ? [state[subObject]] : []);

    // How much this line has allocated so far — over the **stored lines**, a
    // different shape from the rows above. This summed `sumFieldName` too, which
    // only worked while both shapes spelled it the same way. The allocation loop
    // writes `line.quantity` outright, so that is the field, and the two uses no
    // longer have to agree on one name for two things.
    const totalAmount = sumFields(list, 'quantity')

    // Where the rows come from, declared. The endpoint used to be
    // `product/:id/itemLocation` with `:id` substituted from `state.itemId` in
    // this file — one hardcoded parameter, with the name of the field it read
    // written nowhere. It is `${item.itemId}` in the config now, through the
    // same `resolveParams` that builds every process-view request body, so a
    // second parameter is a config change rather than a code change (33.2 §4.2).
    //
    // `item` is the line the modal was opened from and `record` the document it
    // belongs to, which is the vocabulary `optimisticLogicData` already uses.
    const endpoint = resolveParams(element.source?.endpoint, { item: state, record: fullState })

    // A placeholder that resolved to nothing leaves an empty segment. Returning
    // null rather than fetching keeps the old behaviour exactly: the list is
    // empty and the modal shows its emptyMessage, instead of a request for
    // `product//itemLocation`.
    //
    // `source.condition` is the other half of that: a request can be well formed
    // and still not worth making, because the record it depends on has not been
    // chosen yet. `bomInput` said this in code - no `bomVersionId`, show its
    // `noBomMessage` instead of fetching - and it is the predicate language here
    // (33.2 step 10), which also means `lint:conditions` checks it.
    const sourceReady = evaluateCondition(element.source?.condition, { values: fullState })
    const endpointReady = sourceReady
        && typeof endpoint === 'string' && endpoint.length > 0 && !endpoint.includes('//')

    const fetchData = async () => {
        if (!endpointReady) return null;
        const response = await getMe(endpoint);
        return response?.data ? response : null;
    };

    let result = null;

    if (load) {
        result = useQuery({
            queryKey: [entity, endpoint],
            queryFn: () => fetchData(),
            refetchOnMount: true,
        });
    }

    const handleSelectionChange = async (items) => {
        // Flat mode: no subObject, no linesArray — merge mapped fields directly onto line state
        if (!subObject && !linesArray) {
            const item = items && items.length > 0 ? items[0] : null;
            const mappedFields = {};

            if (item && element.valueMap) {
                Object.entries(element.valueMap).forEach(([key, value]) => {
                    if (typeof value === 'string' && value.includes('.')) {
                        const parts = value.split('.');
                        let currentValue = item;
                        for (const part of parts) { currentValue = currentValue?.[part]; }
                        mappedFields[key] = currentValue;
                    } else {
                        mappedFields[key] = item[value];
                    }
                });
            }

            const newState = { ...state, ...mappedFields };
            const newFullState = updateState(fullState, path, newState);
            const processedState = await runLogic(newFullState, entity, prevData);
            setFullState(processedState);
            return;
        }

        // If subObject mode is enabled, map the first (or only) selection into an object
        if (subObject) {
            const item = items && items.length > 0 ? items[0] : null;
            let mappedObject = null;

            if (item) {
                mappedObject = {};
                if (element.valueMap) {
                    Object.entries(element.valueMap).forEach(([key, value]) => {
                        let resolvedValue;
                        if (typeof value === 'string' && value.includes('.')) {
                            const parts = value.split('.');
                            let currentValue = item;
                            for (const part of parts) {
                                currentValue = currentValue?.[part];
                            }
                            resolvedValue = currentValue;
                        } else {
                            resolvedValue = item[value];
                        }
                        if (resolvedValue != null) mappedObject[key] = resolvedValue;
                    });
                }
                // Do NOT set quantity or lineId in subObject mode
            }

            const newState = {
                ...state,
                [subObject]: mappedObject
            };

            const newFullState = updateState(fullState, path, newState);
            const processedState = await runLogic(newFullState, entity, prevData);
            setFullState(processedState);
            return;
        }

        // Default: array mode (linesArray)
        const selectedLines = [];

        // Whether a selection draws down a demand.
        //
        // A location modal allocates: the line wants five, the row holds ten, so
        // it takes five and the next row gets nothing. A BOM input does not -
        // ticking a component copies its quantity across, which `valueMap`
        // already says. The loop was unconditional and `Math.min(x, undefined)`
        // is NaN, which is the whole reason `bomInput` could not use this widget.
        // 1.8 asked for a declared `allocate`; this is it (33.2 step 10).
        //
        // `lineId` and `lineIndex` are stamped here too, because they are what an
        // allocation is - the row this line took, and which line took it. A BOM
        // input is not one, and `routingVersions` is `additionalProperties:
        // false`, so stamping them there would be rejected rather than untidy.
        const allocates = element.allocate === 'constraint';

        if (items && items.length > 0) {
            let remainingConstraint = constraint;

            for (const item of items) {
                if (allocates && remainingConstraint <= 0) break;

                const line = {};

                // Map all values specified in valueMap
                if (element.valueMap) {
                    Object.entries(element.valueMap).forEach(([key, value]) => {
                        if (typeof value === 'string' && value.includes('.')) {
                            const parts = value.split('.');
                            let currentValue = item;
                            for (const part of parts) {
                                currentValue = currentValue?.[part];
                            }
                            line[key] = currentValue;
                        } else {
                            line[key] = item[value];
                        }
                    });
                }

                if (allocates) {
                    // What this row takes, by the same rule the list used to
                    // decide the checkbox was still enabled.
                    const allocatedQuantity =
                        allocationFor(item, sumFieldName, remainingConstraint);
                    remainingConstraint = Math.max(0, remainingConstraint - allocatedQuantity);

                    line.quantity = allocatedQuantity;
                // `lineId` identifies this selection: `<line>.<nth on that
                // line>`. It stays, because the unreserve rule matches
                // line-locations by it. What went is the other job it had —
                // `MultiList` used to split it to find out which line a
                // reservation belonged to, an encoding whose grammar was written
                // in neither file. The line is `lineIndex`, a field, and the
                // partition compares it with `equals` (33.2 steps 7 and 8).
                    line.lineId = `${lineId}.${selectedLines.length}`;
                    line.lineIndex = lineId;
                }
                selectedLines.push(line);
            }
        }

        const newState = {
            ...state,
            [linesArray]: selectedLines
        };

        // How many rows are selected, written back onto the line so its collapsed
        // row can say so. `bomInput` kept `inputsCount` in step with its list by
        // hand; the element names the field.
        if (element.countField) newState[element.countField] = selectedLines.length;

        const newFullState = updateState(fullState, path, newState);
        const processedState = await runLogic(newFullState, entity, prevData);
        setFullState(processedState);
    };

    // Tabs are the same widget twice, over different rows of the same record.
    //
    // `tabList` was a widget for this: a component that read a tab strip out of
    // `tabViewData`, mapped each entry to a hardcoded `ReservationList` through a
    // `componentMap`, and split the chrome (label, icon) from the content
    // (columns, action, filter) across two model files. A tab is a `multiSelect`
    // with its own source and its own predicate, so it is this one, recursed
    // (33.2 step 10b).
    if (Array.isArray(element.tabs)) {
        const { tabs, ...base } = element
        const config = tabs.map(tab => ({
            ...tab,
            content: (
                <MultiSelect
                    entity={entity}
                    validated={validated}
                    useStore={useStore}
                    element={{ ...base, ...tab }}
                    lineId={lineId}
                    path={path}
                />
            ),
        }))
        return <Tabs config={config} id='{type}' type={''} />
    }

    if (!sourceReady) { return <span style={{ fontSize: '0.85rem' }}>{t(element?.notReadyMessage)}</span> }
    if (result?.isLoading) { return <Loader /> }
    if (result?.isError) { return <span>{t(element?.emptyMessage)}</span> }

    return (
        <ModalContainer>
            {totalAmount !== state['itemQuantity'] && header?.description && (
                <Header>
                    <div>
                        {m(header.description, params)}
                    </div>
                </Header>
            )}
            <Body>
                {element.list === 'multiList' && <MultiList
                    data={element.source?.from
                        ? rowsOf(element.source, fullState)
                        : rowsFrom(result?.data, element.source)}
                    onSelectionChange={handleSelectionChange}
                    sumField={sumFieldName}
                    constraint={constraint}
                    columns={col}
                    existingSelections={existingSelectionsComputed}
                    emptyMessage={element.emptyMessage}
                    lineId={lineId}
                    docId={docId}
                    listItems={element.listItems}
                    testId={testId}
                    singleSelect={isSingleSelect}
                    partitions={element.partitions}
                    identity={element.identity}
                    selectable={element.selectable}
                    record={fullState}
                />}
            </Body>
            {footer && <Footer>{m(footer.description, footerParams)}</Footer>}
            {element.action && <Action action={element.action} entity={entity} useStore={useStore} />}
            {footer?.warning && <Warning warning={footer.warning} state={state} compareValue={totalAmount} />}
        </ModalContainer>
    );
};

export default MultiSelect;
